<template>
  <div class="mb-5 pb-5">
    <div v-if="!model.districtHasAssistantsAvailable" class="row mb-4">
      <TextAreaSection
        title="Referir Análisis al Director del CSEE"
        label="Comentario:"
        class="text-area-reason col"
        :value="isCSEEAnalysisProcess?model.returnedReason:model.teacherReason"
        :readonly="true"
      />
    </div>

    <ValidationObserver ref="analysis-general-info">
      <AssistantInformation
        :assistants="model.centerAssistants"
        :collections="collections"
        :submitted="readOnly"
        :has-assistant-available.sync="model.centerHasAssistantsAvailable"
        :validate.sync="invalidAssistant"
        title="Información General"
        question-text="¿La ORE tiene el recurso disponible?"
      >
        <template v-slot:reason>
          <div class="row p-3">
            <div class="col-12 mt-2">
              <label class="input-label">Explique:</label>
              <ValidationProvider
                v-slot="{ errors }"
                name="centerReason"
                :rules="'required'"
              >
                <CustomTextArea
                  id="centerReason"
                  v-model="model.centerReason"
                  style="width:100%"
                  :maxlength="3000"
                  :classes="['eq-field', 'text-right']"
                  :rows="5"
                  :error="errors[0]"
                  :custom-error-msg="'Favor escriba una descripción.'"
                  show-char-count
                  :disabled="readOnly"
                />
              </ValidationProvider>
            </div>
          </div>
        </template>
      </AssistantInformation>
    </ValidationObserver>
    <template v-if="model.centerHasAssistantsAvailable">
      <ValidationObserver ref="analysis-location-info">
        <AssistantLocation
          :assistant-location="model.centerAssistantLocation"
          :academic-year-id="model.academicYear.id"
          :collections="collections"
          class="mt-5"
          :readonly="readOnly"
        />
      </ValidationObserver>
      <DocumentsForm
        :documents="model.centerDocuments"
        :allowed-document-types="allowedDocumentTypes"
        :multiple-uploads-document-type-ids="[requestDocumentTypeCodes.HROtro]"
        :read-only="readOnly"
        info-message="Documentos no son requeridos"
        class="mt-5"
      />
    </template>
    <template v-if="model.centerHasAssistantsAvailable != null">
      <ValidationObserver ref="signature-information-form">
        <ServiceCenterDirectorSignature
          ref="signatureFormRef"
          v-if="canShowSignaturePanel"
          title="Firma del Director CSEE"
          principal-button-label="Presione aquí para añadir firma"
          role-label="Puesto"
          class="mt-5"
          :allow-add-more="allowAddSignature"
          :limit-new-signatures-to="1"
          :cannot-edit-name="true"
          :keep-signed="true"
          :use-role="true"
          :requesters="model.signersCenter"
          :submitted="readOnly"
          :validate.sync="invalidSignature"
        />
      </ValidationObserver>
    </template>
  </div>
</template>

<script>
import CustomTextArea from 'CustomTextArea';
import { ValidationProvider } from 'vee-validate';
import alert from '@/mixin/alert.mixin';
import requestDocumentTypeCodes from '@/utils/constants/hrrequest/requestDocumentTypeCodes';
import ServiceCenterDirectorSignature from '@/components/pei/ubication-tech-assistance/_components/Requesters';
import TextAreaSection from '@/components/pei/ubication-tech-assistance/management/_components/TextAreaSection';
import DocumentsForm from '@/views/humanResources/request/components/DocumentsForm';
import AssistantInformation from '@/views/humanResources/request/components/tabs/components/shared/AssistantInformation';
import { AssistantLocation } from '@/views/humanResources/request/components/tabs/components/tab4';
import { saveCenterAnalysis } from '@/modules/human-resources/api';
import formDataUtil from '@/utils/formDataUtil';
import { requestStatusCodes } from "@/utils/constants/hrrequest/requestStatusCodes";

export default {
  name: 'Tab4ServiceCenterAnalysis',
  mixins: [alert],
  components: {
    ValidationProvider,
    CustomTextArea,
    AssistantInformation,
    AssistantLocation,
    DocumentsForm,
    ServiceCenterDirectorSignature,
    TextAreaSection,
  },
  data() {
    return {
      requestDocumentTypeCodes,
      invalidAssistant: false,
      invalidSignature: false,
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    collections: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    canShowSignaturePanel: {
      type: Boolean,
      default: true,
    }
  },
  computed: {
    allowedDocumentTypes() {
      const documentTypeIds = [
        requestDocumentTypeCodes.HRMinuta,
        requestDocumentTypeCodes.HROtro,
      ];
      return this.collections.documentTypes.filter((docType) =>
        documentTypeIds.includes(docType.id)
      );
    },
    allowAddSignature() {
      return this.model.signersCenter.every((x) => x.isSubmitted);
    },
    model() {
      return this.value;
    },
    isCSEEAnalysisProcess(){ 
      return this.model.statusId == requestStatusCodes.CSEEAnalysisProcess;
    }
  },
  methods: {
    getCenterAnalysisFormData(submitted) {
      const formData = new FormData();

      formDataUtil.build(formData, this.model.id, 'requestId');
      formDataUtil.build(
        formData,
        this.model.centerHasAssistantsAvailable,
        'centerHasAssistantsAvailable'
      );
      if (this.model.centerHasAssistantsAvailable) {
        if (this.model.centerAssistants.length)
          formDataUtil.build(
            formData,
            this.model.centerAssistants.map((assistant) => ({
              id: assistant.id,
              assistantName: assistant.assistantName,
              dailyShiftId: assistant.dailyShift.id,
              resourceTypeId: assistant.resourceType.id,
            })),
            'centerAssistants'
          );
        formDataUtil.build(
          formData,
          this.model.centerAssistantLocation.municipality?.id,
          'assistanceLocationMunicipalityId'
        );
        formDataUtil.build(
          formData,
          this.model.centerAssistantLocation.school?.id,
          'assistanceLocationSchoolId'
        );
        formDataUtil.build(
          formData,
          this.model.centerAssistantLocation?.comment ?? '',
          'assistanceLocationComment'
        );
        formDataUtil.build(formData, this.model.centerDocuments, 'documents');
      } else
        formDataUtil.build(formData, this.model.centerReason, 'centerReason');

      if (this.model.signersCenter.length) {
        formDataUtil.build(
          formData,
          this.model.signersCenter.map((signer) => ({
            id: signer.id || 0,
            requestId: this.model.id,
            fullName: signer.name,
            rolId: signer.role.id,
            telephoneNumber: signer.phone,
            email: signer.email,
            signed: signer.signed,
            isSubmitted: signer.isSubmitted || !!submitted,
          })),
          'signatures'
        );
      }
      return formData;
    },
    async save(submitted) {
      const isValid =
        this.model.centerHasAssistantsAvailable == null ||
        (await this.$refs['signature-information-form']?.validate());

      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor de completar la información solicitada para continuar.',
          'error'
        );
        return;
      }
      await saveCenterAnalysis(this.getCenterAnalysisFormData(submitted))
        .then(() => {
          this.ShowToast(
            'Confirmación',
            'El análisis ha sido guardado exitosamente.',
            'success'
          );
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    async onValidateSignature() {
      if (this.model.centerHasAssistantsAvailable == null) {
        this.invalidAssistant = true;
        return false;
      }

      const formValidated = await this.$refs[
        'signature-information-form'
      ]?.validate();
      this.invalidSignature = this.$refs.signatureFormRef ? !this.$refs.signatureFormRef.hasAnyItems : false;
      this.invalidAssistant =
        this.model.centerHasAssistantsAvailable &&
        !this.model.centerAssistants.length;

      return formValidated && !this.invalidSignature;
    },
  },
  mounted() {},
};
</script>

<style lang="scss" scoped>
::v-deep .text-area-reason textarea {
  min-height: auto !important;
}
</style>
